import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import {
  type QuestionInput,
  questionSchema,
} from '@/features/survey/actions.schema';
import { addQuestion } from '@/features/survey/survey-admin-api';
import { emptyQuestionText, QuestionFields } from './question-fields';

// Ports features/survey/components/add-question.tsx. The Next conform form
// driven by a server action becomes a controlled form calling `addQuestion`;
// the caller navigates to the surveys list via `onSuccess` in place of the
// action's `redirect`.
export default function AddQuestionForm({
  onSuccess,
}: {
  onSuccess: () => void;
}) {
  const t = useTranslations('survey');
  const [value, setValue] = useState<QuestionInput>({
    description: '',
    beforeQuestion: emptyQuestionText(),
    afterQuestion: emptyQuestionText(),
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = questionSchema.safeParse(value);
    if (!submission.success) {
      setError(t('description'));
      return;
    }

    setPending(true);
    try {
      const result = await addQuestion(submission.data);
      if (result.ok) {
        onSuccess();
      } else {
        setError(result.error);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <QuestionFields
        value={value}
        onChange={setValue}
        idPrefix="add-question"
      />
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton label={t('add_question')} pending={pending} />
    </form>
  );
}
